import React, { useState, useEffect } from 'react';
import { StageProps, SeriesParticipant, SeriesData } from '../types';

// Enhanced field configuration interface
interface FieldConfig {
  id: string;
  fieldName: string;
  caption: string;
  type: 'participant' | 'race';
  description: string;
  isSensitive: boolean;
  isSelected: boolean;
  order: number;
}

const DataPublishing: React.FC<StageProps> = ({ appData, setAppData, onPrev }) => {
  const [seriesName, setSeriesName] = useState('');
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ success: boolean; url?: string; error?: string } | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Enhanced field metadata for better UX
  const getFieldMetadata = (fieldName: string, type: 'participant' | 'race') => {
    const participantFields: Record<string, { description: string; isSensitive: boolean; recommended: boolean }> = {
      name: { description: 'Participant name', isSensitive: false, recommended: true },
      totalPoints: { description: 'Total series points', isSensitive: false, recommended: true },
      license: { description: 'License number (may be sensitive)', isSensitive: true, recommended: false },
      racerNumber: { description: 'Racer number', isSensitive: false, recommended: false },
      team: { description: 'Team name', isSensitive: false, recommended: false },
      'Team/Sponsor': { description: 'Team or sponsor name', isSensitive: false, recommended: false },
      'ID': { description: 'Unique identifier (may be sensitive)', isSensitive: true, recommended: false },
      'City': { description: 'City of residence', isSensitive: false, recommended: false },
      'State': { description: 'State or province', isSensitive: false, recommended: false },
      'rank': { description: 'Race position/rank', isSensitive: false, recommended: false },
      'time': { description: 'Race time', isSensitive: false, recommended: false },
      'firstname': { description: 'First name', isSensitive: true, recommended: false },
      'lastname': { description: 'Last name', isSensitive: true, recommended: false },
      position: { description: 'Overall series position', isSensitive: false, recommended: true },
      bestRaces: { description: 'Count of best races (if series uses "best X of Y" rules)', isSensitive: false, recommended: false },
      bestOfPoints: { description: 'Best-of points total', isSensitive: false, recommended: false },
    };

    if (type === 'race') {
      return {
        description: 'Race points and position (combined column)',
        isSensitive: false,
        recommended: true
      };
    }

    return participantFields[fieldName] || {
      description: 'Custom field',
      isSensitive: false,
      recommended: false
    };
  };

  useEffect(() => {
    // Load publishing settings from series data instead of localStorage
    let initialSeriesName = '';
    let initialFieldConfigs: FieldConfig[] = [];

    // Check if we have existing series metadata with publishing settings
    if (appData.currentSeriesMetadata?.publishingSettings) {
      const settings = appData.currentSeriesMetadata.publishingSettings;
      console.log('📋 DataPublishing: Found existing publishing settings:', settings);
      if (settings.fieldConfigs) {
        initialFieldConfigs = settings.fieldConfigs;
        console.log('📋 DataPublishing: Loading saved field configs:', initialFieldConfigs.length, 'configs');
        console.log('📋 DataPublishing: Field configs preview:', initialFieldConfigs.slice(0, 3).map(c => ({ fieldName: c.fieldName, caption: c.caption, isSelected: c.isSelected, order: c.order })));
      } else {
        console.log('📋 DataPublishing: No field configs found in publishing settings');
      }
    } else {
      console.log('📋 DataPublishing: No publishing settings found in series metadata');
      console.log('📋 DataPublishing: Current series metadata:', appData.currentSeriesMetadata);
    }

    // Set initial series name from metadata or use default
    if (appData.currentSeriesMetadata?.name) {
      initialSeriesName = appData.currentSeriesMetadata.name;
    }

    // Extract available fields and races from the final results
    if (appData.finalResults && appData.finalResults.length > 0) {
      const sample = appData.finalResults[0];
      
      // Extract participant fields (excluding races, internal fields, and category since it's used for grouping)
      const fields = Object.keys(sample).filter(key => 
        key !== 'id' && key !== 'races' && key !== 'bestRaces' && key !== 'category'
      );
      
      // Extract available races by scanning ALL participants (not just the first one)
      const raceSet = new Set<string>();
      appData.finalResults.forEach(participant => {
        if (participant.races && typeof participant.races === 'object') {
          Object.keys(participant.races).forEach(raceId => {
            raceSet.add(raceId);
          });
        }
      });
      const races = Array.from(raceSet).sort();
      const bestOfCount = appData.seriesConfiguration?.bestOfCount || appData.currentSeriesMetadata?.bestOfRaces;
      
      console.log('📋 DataPublishing: Detected races from ALL participants:', races);
      
      // If we don't have saved field configs, create default ones
      if (initialFieldConfigs.length === 0) {
        const configs: FieldConfig[] = [];
        let order = 0;

        // Add participant fields
        fields.forEach(fieldName => {
          const metadata = getFieldMetadata(fieldName, 'participant');
          configs.push({
            id: `field_${fieldName}`,
            fieldName,
            caption: fieldName === 'bestOfPoints' && bestOfCount ? `Best ${bestOfCount} Points` : fieldName,
            type: 'participant',
            description: metadata.description,
            isSensitive: metadata.isSensitive,
            isSelected: fieldName === 'bestOfPoints' ? !!bestOfCount : metadata.recommended,
            order: order++
          });
        });

        // Add race fields
        races.forEach(raceName => {
          const metadata = getFieldMetadata(raceName, 'race');
          configs.push({
            id: `race_${raceName}`,
            fieldName: raceName,
            caption: raceName, // Default caption to race name
            type: 'race',
            description: metadata.description,
            isSensitive: metadata.isSensitive,
            isSelected: metadata.recommended, // Auto-select race columns
            order: order++
          });
        });

        initialFieldConfigs = configs;
      } else {
        // Drop race columns that no longer exist (deleted events)
        initialFieldConfigs = initialFieldConfigs.filter(
          (c) => c.type !== 'race' || races.includes(c.fieldName)
        );
        // We have saved configs, but need to sync with current data
        // Add any new fields that weren't in the saved config
        const existingFieldNames = new Set(initialFieldConfigs.map(c => c.fieldName));
        let maxOrder = Math.max(...initialFieldConfigs.map(c => c.order), -1);

        // Check for new participant fields
        fields.forEach(fieldName => {
          if (!existingFieldNames.has(fieldName)) {
            const metadata = getFieldMetadata(fieldName, 'participant');
            initialFieldConfigs.push({
              id: `field_${fieldName}`,
              fieldName,
              caption: fieldName === 'bestOfPoints' && bestOfCount ? `Best ${bestOfCount} Points` : fieldName,
              type: 'participant',
              description: metadata.description,
              isSensitive: metadata.isSensitive,
              isSelected: fieldName === 'bestOfPoints' ? !!bestOfCount : metadata.recommended,
              order: ++maxOrder
            });
          }
        });

        // Check for new race fields
        races.forEach(raceName => {
          if (!existingFieldNames.has(raceName)) {
            const metadata = getFieldMetadata(raceName, 'race');
            initialFieldConfigs.push({
              id: `race_${raceName}`,
              fieldName: raceName,
              caption: raceName,
              type: 'race',
              description: metadata.description,
              isSensitive: metadata.isSensitive,
              isSelected: metadata.recommended, // Auto-select new race fields (they're recommended)
              order: ++maxOrder
            });
          }
        });

        if (bestOfCount) {
          initialFieldConfigs = initialFieldConfigs.map((c) =>
            c.fieldName === 'bestOfPoints'
              ? { ...c, isSelected: true, caption: `Best ${bestOfCount} Points` }
              : c
          );
        }

        // Sort by order
        initialFieldConfigs.sort((a, b) => a.order - b.order);
      }
    }

    setSeriesName(initialSeriesName);
    setFieldConfigs(initialFieldConfigs);
    
    console.log('📋 DataPublishing: Final field configs set:', initialFieldConfigs.length, 'configs');
    console.log('📋 DataPublishing: Selected configs:', initialFieldConfigs.filter(c => c.isSelected).map(c => ({ fieldName: c.fieldName, caption: c.caption, order: c.order })));
  }, [appData.finalResults, appData.currentSeriesMetadata, appData.seriesConfiguration?.bestOfCount]);

  // Helper function to get effective points (best-of or total)
  const getEffectivePoints = (participant: SeriesParticipant) => {
    return participant.bestOfPoints || participant.totalPoints;
  };

  useEffect(() => {
    // Generate preview data whenever field configs or data changes
    if (appData.finalResults && fieldConfigs.length > 0) {
      const selectedConfigs = fieldConfigs.filter(config => config.isSelected).sort((a, b) => a.order - b.order);
      console.log('📊 Preview generation - selected configs order:', selectedConfigs.map(c => ({ fieldName: c.fieldName, order: c.order, type: c.type })));
      
      // Sort participants by effective points for correct preview ranking
      const sortedParticipants = [...appData.finalResults]
        .sort((a, b) => getEffectivePoints(b) - getEffectivePoints(a));
      
      const preview = sortedParticipants.slice(0, 5).map((participant, index) => {
        const row: any = {
          categoryPosition: index + 1, // Correct position based on effective points ranking
        };
        
        selectedConfigs.forEach(config => {
          if (config.type === 'participant') {
            // Always use the actual field value - don't override totalPoints
            const bestOfCount = appData.seriesConfiguration?.bestOfCount || appData.currentSeriesMetadata?.bestOfRaces;
            const bestOfColumnSelected = selectedConfigs.some((c) => c.fieldName === 'bestOfPoints');
            if (config.fieldName === 'totalPoints' && bestOfCount && !bestOfColumnSelected) {
              row[config.fieldName] = getEffectivePoints(participant);
            } else {
              row[config.fieldName] = participant[config.fieldName as keyof SeriesParticipant] || '-';
            }
          } else if (config.type === 'race') {
            if (participant.races && participant.races[config.fieldName]) {
              row[`${config.fieldName}_combined`] = {
                points: participant.races[config.fieldName].points,
                position: participant.races[config.fieldName].position
              };
            } else {
              row[`${config.fieldName}_combined`] = null;
            }
          }
        });
        
        return row;
      });
      
      setPreviewData(preview);
    }
  }, [appData.finalResults, fieldConfigs, appData.currentSeriesMetadata?.bestOfRaces, appData.seriesConfiguration?.bestOfCount]);

  // Helper function to save publishing settings to series data
  const savePublishingSettingsToSeries = async (newSeriesName?: string, newFieldConfigs?: FieldConfig[]) => {
    console.log('📋 DataPublishing: savePublishingSettingsToSeries called');
    console.log('📋 DataPublishing: newSeriesName:', newSeriesName);
    console.log('📋 DataPublishing: newFieldConfigs count:', newFieldConfigs?.length || fieldConfigs.length);
    console.log('📋 DataPublishing: currentSeriesMetadata exists:', !!appData.currentSeriesMetadata);
    
    try {
      const dataService = (await import('../services/dataService')).default.getInstance();
      
      if (appData.currentSeriesMetadata) {
        // Update existing series
        const currentSeries = await dataService.loadSeries(appData.currentSeriesMetadata.id);
        const updatedSeries: SeriesData = {
          ...currentSeries,
          publishingSettings: {
            isPublished: false,
            ...currentSeries.publishingSettings,
            fieldConfigs: newFieldConfigs || fieldConfigs,
          }
        };
        
        // Update series name if provided
        if (newSeriesName !== undefined) {
          updatedSeries.metadata.name = newSeriesName;
        }
        
        await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeries);
        
        // Update local app data with the saved publishing settings
        setAppData({
          ...appData,
          currentSeriesMetadata: {
            ...appData.currentSeriesMetadata!,
            publishingSettings: updatedSeries.publishingSettings,
            name: newSeriesName !== undefined ? newSeriesName : appData.currentSeriesMetadata!.name,
            lastModified: new Date().toISOString()
          }
        });
        
        console.log('Publishing settings saved and app data updated');
      }
      // Note: For new series, settings will be saved when publishing
    } catch (error) {
      console.warn('Failed to save publishing settings to series:', error);
      // Continue without breaking the UI
    }
  };

  // Update field configuration
  const updateFieldConfig = (id: string, updates: Partial<FieldConfig>) => {
    console.log('📋 DataPublishing: Updating field config:', id, updates);
    setFieldConfigs(prev => {
      const updated = prev.map(config => 
        config.id === id ? { ...config, ...updates } : config
      );
      
      console.log('📋 DataPublishing: Field configs after update:', updated.filter(c => c.isSelected).length, 'selected');
      
      // Save to series data instead of localStorage
      savePublishingSettingsToSeries(undefined, updated);
      return updated;
    });
  };

  // Handle drag and drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const updatedConfigs = [...fieldConfigs];
    const draggedConfig = updatedConfigs[draggedIndex];
    
    // Remove from old position
    updatedConfigs.splice(draggedIndex, 1);
    // Insert at new position
    updatedConfigs.splice(dropIndex, 0, draggedConfig);
    
    // Update order values
    updatedConfigs.forEach((config, index) => {
      config.order = index;
    });

    console.log('🔄 Updated field order:', updatedConfigs.map(c => ({ fieldName: c.fieldName, order: c.order, isSelected: c.isSelected })));

    setFieldConfigs(updatedConfigs);
    setDraggedIndex(null);
    
    // Force preview regeneration with the new configs
    if (appData.finalResults && updatedConfigs.length > 0) {
      const selectedConfigs = updatedConfigs.filter(config => config.isSelected).sort((a, b) => a.order - b.order);
      console.log('🔄 Force regenerating preview with new order:', selectedConfigs.map(c => ({ fieldName: c.fieldName, order: c.order, type: c.type })));
      
             // Sort participants by effective points for correct drag preview ranking
      const sortedParticipants = [...appData.finalResults]
        .sort((a, b) => getEffectivePoints(b) - getEffectivePoints(a));
        
      const preview = sortedParticipants.slice(0, 5).map((participant, index) => {
         const row: any = {
           categoryPosition: index + 1, // Correct position based on effective points ranking
         };
         
         selectedConfigs.forEach(config => {
           if (config.type === 'participant') {
             // Always use the actual field value - don't override totalPoints
             row[config.fieldName] = participant[config.fieldName as keyof SeriesParticipant] || '-';
           } else if (config.type === 'race') {
            if (participant.races && participant.races[config.fieldName]) {
              row[`${config.fieldName}_combined`] = {
                points: participant.races[config.fieldName].points,
                position: participant.races[config.fieldName].position
              };
            } else {
              row[`${config.fieldName}_combined`] = null;
            }
          }
        });
        
        return row;
      });
      
      setPreviewData(preview);
    }
    
    // Save to series data instead of localStorage
    savePublishingSettingsToSeries(undefined, updatedConfigs);
  };

  const handleSeriesNameChange = (name: string) => {
    setSeriesName(name);
    savePublishingSettingsToSeries(name);
  };

  const generatePublicData = () => {
    if (!appData.finalResults) return null;

    const selectedConfigs = fieldConfigs.filter(config => config.isSelected).sort((a, b) => a.order - b.order);
    const selectedFields = selectedConfigs.filter(c => c.type === 'participant').map(c => c.fieldName);
    const selectedRaces = selectedConfigs.filter(c => c.type === 'race').map(c => c.fieldName);
    const fieldCaptions = selectedConfigs.reduce((acc, config) => {
      acc[config.fieldName] = config.caption;
      return acc;
    }, {} as Record<string, string>);

    // Sort participants by effective points for correct published ranking
    const sortedParticipants = [...appData.finalResults]
      .sort((a, b) => getEffectivePoints(b) - getEffectivePoints(a));

    const bestOfCount = appData.seriesConfiguration?.bestOfCount || appData.currentSeriesMetadata?.bestOfRaces;
    const bestOfColumnSelected = selectedFields.includes('bestOfPoints');

    const publicData = {
      seriesName: seriesName || 'Race Series',
      generatedAt: new Date().toISOString(),
      selectedFields, // Include selected fields for backend processing
      selectedRaces, // Include selected races for backend processing
      fieldCaptions, // Include custom captions for backend processing
      fieldOrder: selectedConfigs.map(c => ({ fieldName: c.fieldName, type: c.type, order: c.order })), // Unified field ordering
      participants: sortedParticipants.map((participant, index) => {
        const row: any = {
          position: index + 1, // Correct position based on effective points ranking
          category: participant.category, // Always include category for grouping
        };
        
        // Add selected participant fields
        selectedFields.forEach(field => {
          if (field === 'totalPoints' && bestOfCount && !bestOfColumnSelected) {
            row[field] = getEffectivePoints(participant);
          } else {
            row[field] = participant[field as keyof SeriesParticipant];
          }
        });
        
        // Add best-of metadata for Lambda processing
        row.bestOfPoints = participant.bestOfPoints;
        row.effectivePoints = getEffectivePoints(participant);
        
        // Add races object for backend compatibility
        if (selectedRaces.length > 0) {
          row.races = {};
          selectedRaces.forEach(raceId => {
            if (participant.races && participant.races[raceId]) {
              row.races[raceId] = participant.races[raceId];
            }
          });
        }
        
        return row;
      })
    };

    return publicData;
  };

  const downloadPreviewJson = () => {
    const data = generatePublicData();
    if (!data) return;

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${seriesName || 'series'}_public_data.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePublish = async () => {
    const selectedConfigs = fieldConfigs.filter(c => c.isSelected);
    if (selectedConfigs.length === 0 || !seriesName.trim()) {
      return;
    }

    setIsPublishing(true);
    setPublishResult(null);

    try {
      // Prepare publish data
      const publishData = generatePublicData();
      if (!publishData) {
        throw new Error('Failed to generate publish data');
      }

      // For development/demo - use mock endpoint or actual Lambda URL
      const PUBLISH_API_URL = process.env.REACT_APP_PUBLISH_API_URL || 
        'https://your-lambda-url.execute-api.us-east-1.amazonaws.com/prod/publish';

      console.log('Publishing to:', PUBLISH_API_URL);
      console.log('Publish data:', publishData);

      // Check if we're using the placeholder URL (development mode)
      if (PUBLISH_API_URL.includes('your-lambda-url')) {
        console.log('Development mode: Simulating publish without actual HTTP request');
        
        // Simulate a delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Generate a mock URL
        const mockUrl = `https://race-series-demo.com/series/${encodeURIComponent(seriesName.toLowerCase().replace(/\s+/g, '-'))}`;
        
        setPublishResult({
          success: true,
          url: mockUrl
        });
        
        return;
      }

      // Save series data first
      const dataService = (await import('../services/dataService')).default.getInstance();
      
      // Determine if we're updating an existing series or creating a new one
      let seriesData: SeriesData;
      if (appData.currentSeriesMetadata) {
        // Update existing series
        console.log('Updating existing series:', appData.currentSeriesMetadata.id);
        seriesData = {
          metadata: {
            ...appData.currentSeriesMetadata,
            name: seriesName, // Allow series name to be updated
            lastModified: new Date().toISOString(),
            pointsScheme: appData.pointsScheme, // Ensure points scheme is saved to metadata
          },
          participants: appData.finalResults || [],
          pointsScheme: appData.pointsScheme,
          publishingSettings: {
            fieldConfigs,
            isPublished: true,
          },
        };
        
        // Use updateSeries for existing series
        await dataService.updateSeries(appData.currentSeriesMetadata.id, seriesData);
        console.log('Existing series updated successfully');
        
        // Update local app data with the saved publishing settings
        setAppData({
          ...appData,
          currentSeriesMetadata: {
            ...seriesData.metadata,
            publishingSettings: seriesData.publishingSettings
          }
        });
      } else {
        // Create new series
        console.log('Creating new series');
        seriesData = dataService.createNewSeries(seriesName, appData.pointsScheme);
        seriesData.participants = appData.finalResults || [];
        seriesData.publishingSettings = {
          fieldConfigs,
          isPublished: true,
        };
        
        // Use saveSeries for new series
        await dataService.saveSeries(seriesData);
        console.log('New series created successfully');
        
        // Update local app data with the new series metadata
        setAppData({
          ...appData,
          currentSeriesMetadata: seriesData.metadata
        });
      }

      const response = await fetch(PUBLISH_API_URL, {
        method: 'POST',
        headers: await dataService.getHeaders(),
        body: JSON.stringify(publishData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.url) {
        // Update the series data again with the published URL
        const updatedPublishingSettings = {
          fieldConfigs,
          isPublished: true,
          publishedUrl: result.url,
          lastPublished: new Date().toISOString()
        };

        if (appData.currentSeriesMetadata) {
          // Update existing series with published URL
          const updatedSeriesData = {
            ...seriesData,
            publishingSettings: updatedPublishingSettings
          };
          await dataService.updateSeries(appData.currentSeriesMetadata.id, updatedSeriesData);
          console.log('Series updated with published URL:', result.url);
          
          // CRITICAL: Update the local app data with the new series metadata
          setAppData({
            ...appData,
            currentSeriesMetadata: {
              ...appData.currentSeriesMetadata,
              publishingSettings: updatedPublishingSettings,
              name: seriesName, // Update name if it was changed
              lastModified: new Date().toISOString()
            }
          });
          
        } else {
          // Update new series with published URL
          const updatedSeriesData = {
            ...seriesData,
            publishingSettings: updatedPublishingSettings
          };
          await dataService.saveSeries(updatedSeriesData);
          console.log('New series saved with published URL:', result.url);
          
          // CRITICAL: Update the local app data with the new series metadata
          setAppData({
            ...appData,
            currentSeriesMetadata: updatedSeriesData.metadata
          });
        }

        setPublishResult({
          success: true,
          url: result.url
        });
      } else {
        throw new Error(result.error || 'Unknown publishing error');
      }

    } catch (error: any) {
      console.error('Publishing error:', error);
      
      let errorMessage = 'An unexpected error occurred while publishing.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to the publishing service. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setPublishResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>🌐 Public Data Publishing</h2>
        <p>Configure which fields to include in your public race series report. Sensitive information like license numbers are excluded by default.</p>
      </div>

      {/* Series Name Input */}
      <div className="form-group">
        <label htmlFor="seriesName">Series Name</label>
        <input
          id="seriesName"
          type="text"
          className="form-control"
          value={seriesName}
          onChange={(e) => handleSeriesNameChange(e.target.value)}
          placeholder="Enter series name for public display..."
          style={{ 
            fontSize: '1.1rem', 
            fontWeight: '600',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        />
        <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
          This will appear as the title of your public results page
        </small>
      </div>

      {/* Privacy Notice */}
      <div className="info-box" style={{ backgroundColor: '#e6fffa', border: '1px solid #38b2ac' }}>
        <h4>🔒 Privacy & Security</h4>
        <p>
          <strong>Recommended fields</strong> ✅ are safe for public display.<br />
          <strong>Sensitive fields</strong> ⚠️ may contain personal information and should be carefully considered.<br />
          Always review your organization's privacy policy before publishing participant data.
        </p>
      </div>

      {/* Enhanced Field Selection Table */}
      <div className="form-group">
        <label>Configure Fields to Include</label>
        <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1rem' }}>
          Drag and drop to reorder fields, edit captions for custom column headers, and select which fields to publish.
        </p>
        
        <div style={{ 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px', 
          overflow: 'hidden',
          backgroundColor: 'white'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '40px 1fr 1fr 2fr 60px',
            gap: '0',
            backgroundColor: '#f7fafc',
            borderBottom: '2px solid #e2e8f0',
            padding: '0.75rem',
            fontWeight: '600',
            fontSize: '0.9rem',
            color: '#4a5568'
          }}>
            <div>Order</div>
            <div>Field</div>
            <div>Caption</div>
            <div>Type</div>
            <div>Publish</div>
          </div>
          
          {fieldConfigs.map((config, index) => (
            <div
              key={config.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1fr 2fr 60px',
                gap: '0',
                padding: '0.75rem',
                borderBottom: '1px solid #cbd5e0',
                backgroundColor: draggedIndex === index 
                  ? '#edf2f7' 
                  : hoveredIndex === index
                    ? '#e2e8f0'
                    : config.isSelected
                      ? 'white'
                      : '#f1f5f9',
                cursor: 'grab',
                transition: 'background-color 0.2s ease'
              }}
            >
              {/* Drag Handle */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#a0aec0',
                fontSize: '1.2rem'
              }}>
                ⋮⋮
              </div>
              
              {/* Field Name */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                {config.type === 'race' ? '🏁' : '👤'} {config.fieldName}
              </div>
              
              {/* Editable Caption */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  value={config.caption}
                  onChange={(e) => updateFieldConfig(config.id, { caption: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    backgroundColor: '#f7fafc'
                  }}
                  placeholder={config.fieldName}
                />
              </div>
              
              {/* Type Description */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.85rem',
                color: '#718096',
                paddingLeft: '1rem'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {getFieldMetadata(config.fieldName, config.type).recommended && <span style={{ color: '#38a169' }}>✅</span>}
                  {config.isSensitive && <span style={{ color: '#d69e2e' }}>⚠️</span>}
                  {config.description}
                </span>
              </div>
              
              {/* Publish Checkbox */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <input
                  type="checkbox"
                  checked={config.isSelected}
                  onChange={(e) => updateFieldConfig(config.id, { isSelected: e.target.checked })}
                  style={{ 
                    transform: 'scale(1.2)',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        
        <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
          💡 Captions will be used as column headers in the published results. Drag the ⋮⋮ handle to reorder fields.
        </small>
      </div>

      {/* Development Mode Notice */}
      {(!process.env.REACT_APP_PUBLISH_API_URL || process.env.REACT_APP_PUBLISH_API_URL.includes('your-lambda-url')) && (
        <div className="info-box" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }}>
          <h4>🔧 Development Mode</h4>
          <p>
            <strong>Publishing Service Not Configured:</strong> The system will simulate publishing without actually deploying to the web.<br />
            To enable real publishing, set your <code>REACT_APP_PUBLISH_API_URL</code> environment variable to your Lambda endpoint.
          </p>
        </div>
      )}



      {/* Live Preview */}
      {previewData.length > 0 && (
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label>📋 Live Preview</label>
            <button 
              className="btn btn-secondary" 
              onClick={downloadPreviewJson}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              📄 Download JSON
            </button>
          </div>
          
          <div style={{ 
            backgroundColor: 'white',
            border: '1px solid #e2e8f0', 
            borderRadius: '16px', 
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header matching published results */}
            <div style={{
              background: '#4299e1',
              color: 'white',
              padding: '0.75rem 2rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '60px'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>ZONE4</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ fontSize: '1rem', fontWeight: '600' }}>Series Points Manager</div>
                <div style={{ height: '28px', width: '28px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px' }}></div>
              </div>
            </div>
            
            {/* Series header */}
            <div style={{
              background: 'linear-gradient(135deg, #2780E3 0%, #1B70CF 100%)',
              color: 'white',
              padding: '1.5rem 2rem',
              textAlign: 'center'
            }}>
              <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                {seriesName || 'Untitled Series'}
              </h1>
              <div style={{ fontSize: '1.1rem', opacity: '0.9' }}>Official Race Series Results</div>
            </div>

            {/* Preview content */}
            <div style={{ padding: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>🏆 Series Standings Preview</h3>
              
              <div style={{
                background: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)' }}>
                      <th style={{ 
                        padding: '1rem 0.75rem', 
                        textAlign: 'left', 
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: '#2d3748',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Position
                      </th>
                                            {fieldConfigs.filter(c => c.isSelected).sort((a, b) => a.order - b.order).map((config, headerIndex) => {
                         if (headerIndex === 0) {
                           const headerOrder = fieldConfigs.filter(c => c.isSelected).sort((a, b) => a.order - b.order);
                           console.log('🏷️ UNIFIED Table header order:', headerOrder.map(c => `${c.fieldName}(${c.type})`));
                           console.log('🏷️ totalPoints position in unified headers:', headerOrder.findIndex(c => c.fieldName === 'totalPoints'));
                         }
                         return (
                        <th key={config.id} style={{ 
                          padding: '1rem 0.75rem', 
                          textAlign: config.type === 'race' ? 'center' : 'left', 
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: '#2d3748',
                          borderBottom: '2px solid #e2e8f0'
                        }}>
                          {config.caption}
                        </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index} style={{ 
                        backgroundColor: index % 2 === 0 ? 'white' : '#f7fafc',
                        transition: 'background-color 0.2s ease'
                      }}>
                        <td style={{ 
                          padding: '0.75rem',
                          borderBottom: '1px solid #f7fafc',
                          verticalAlign: 'middle'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600', fontSize: '1.1rem' }}>
                            <strong>{row.categoryPosition}</strong>
                            {index === 0 && <span style={{ fontSize: '1.2rem' }}>🥇</span>}
                            {index === 1 && <span style={{ fontSize: '1.2rem' }}>🥈</span>}
                            {index === 2 && <span style={{ fontSize: '1.2rem' }}>🥉</span>}
                          </div>
                        </td>
                                                {fieldConfigs.filter(c => c.isSelected).sort((a, b) => a.order - b.order).map((config, cellIndex) => {
                          if (cellIndex === 0 && index === 0) {
                            const bodyOrder = fieldConfigs.filter(c => c.isSelected).sort((a, b) => a.order - b.order);
                            console.log('🔢 UNIFIED Table body order:', bodyOrder.map(c => `${c.fieldName}(${c.type})`));
                            console.log('🔢 totalPoints position in unified body:', bodyOrder.findIndex(c => c.fieldName === 'totalPoints'));
                          }
                          
                          if (config.type === 'participant') {
                            return (
                            <td key={config.id} style={{
                              padding: '0.75rem',
                              borderBottom: '1px solid #f7fafc',
                              verticalAlign: 'middle',
                              fontWeight: config.fieldName === 'name' ? '600' : 'normal',
                              color: config.fieldName === 'totalPoints' ? '#2780E3' : '#2d3748'
                            }}>
                              {config.fieldName === 'totalPoints' && row[config.fieldName] ? `${row[config.fieldName]} pts` : (row[config.fieldName] !== undefined ? row[config.fieldName] : '-')}
                            </td>
                            );
                          } else if (config.type === 'race') {
                            return (
                            <td key={config.id} style={{ 
                              padding: '0.75rem',
                              borderBottom: '1px solid #f7fafc',
                              verticalAlign: 'middle',
                              textAlign: 'center'
                            }}>
                              {row[`${config.fieldName}_combined`] ? (
                                <div style={{ fontSize: '0.9rem' }}>
                                  <span style={{ 
                                    fontWeight: '600', 
                                    color: '#38a169',
                                    display: 'block',
                                    fontSize: '1rem'
                                  }}>
                                    {row[`${config.fieldName}_combined`].points}pts
                                  </span>
                                  <small style={{ 
                                    fontSize: '0.8rem',
                                    color: '#718096',
                                    marginTop: '0.2rem',
                                    display: 'block'
                                  }}>
                                    P{row[`${config.fieldName}_combined`].position}
                                  </small>
                                </div>
                              ) : (
                                <span style={{ color: '#a0aec0' }}>-</span>
                              )}
                            </td>
                                                         );
                           } else {
                             return null;
                           }
                         })}
                       </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
            💡 This preview matches how your data will appear in the published results with the same styling and layout
          </small>
        </div>
      )}

      <div className="stage-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          ← Back to Results
        </button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className="btn btn-primary"
            disabled={fieldConfigs.filter(c => c.isSelected).length === 0 || !seriesName.trim() || isPublishing}
            onClick={handlePublish}
          >
            {isPublishing ? (
              <>
                <span style={{ marginRight: '0.5rem' }}>⏳</span>
                Publishing...
              </>
            ) : (
              <>
                🚀 Publish to Web
              </>
            )}
          </button>
        </div>
      </div>

      {/* Publish Result - Appears BELOW the publish button for immediate visibility */}
      {publishResult && (
        <div className={`info-box ${publishResult.success ? 'success' : 'error'}`} style={{
          backgroundColor: publishResult.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${publishResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          color: publishResult.success ? '#155724' : '#721c24',
          marginTop: '1rem'
        }}>
          {publishResult.success ? (
            <div>
              <h4>✅ Successfully Published!</h4>
              <p>
                Your race series results have been published and are now live.
              </p>
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                backgroundColor: 'rgba(255,255,255,0.9)', 
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <strong>📄 Published Results:</strong><br />
                <a 
                  href={publishResult.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ 
                    color: '#155724', 
                    textDecoration: 'underline',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    display: 'inline-block',
                    marginTop: '0.5rem'
                  }}
                >
                  🔗 Click to View Published Results
                </a>
                <br />
                <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
                  {publishResult.url}
                </small>
              </div>
            </div>
          ) : (
            <div>
              <h4>❌ Publishing Failed</h4>
              <p><strong>Error:</strong> {publishResult.error}</p>
              <small>Please check your configuration and try again.</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataPublishing;